import { CLASSY_HREF } from './crawler.constants'
import { ModalSDK } from '../modal/modal.types'
import { strategyHandlers } from './crawler.element.strategies'
import { ComponentBase } from '../config/config.types'
import HostPageEventInstrumentation from '../host-page-events/host-page-event.instrumentation'
import {
  DEFAULT_INLINE_GRID_DECISION,
  InlineGridDecision,
  InlineGridPath,
  makeInlineGridDecision,
} from '../inline-grid/inline-grid-decision'

// The renderer receives the element instance so the current and future strategies
// share the same lifecycle and replacement contract.
// eslint-disable-next-line no-use-before-define
export type InlineGridRenderer = (element: CrawlerElement) => void
export type InlineGridRenderers = Partial<Record<InlineGridPath, InlineGridRenderer>>

interface CrawlerElementProps {
  win: Window
  el: HTMLElement
  modalSDK: ModalSDK
  config: ComponentBase | null
  campaignId: string
  hostPageEventInstrumentation?: HostPageEventInstrumentation
  inlineGridDecision?: InlineGridDecision
  inlineGridRenderers?: InlineGridRenderers
  inlineGridProfile?: unknown
  organizationName?: string
  pgdid?: string
}

export default class CrawlerElement {
  private props: CrawlerElementProps
  private destroyed = false
  private originalElement: HTMLElement | null = null
  private replacementElement: HTMLElement | null = null
  private cleanupCallbacks = new Set<() => void>()
  private resolvedInlineGridDecision: InlineGridDecision = DEFAULT_INLINE_GRID_DECISION

  constructor(props: CrawlerElementProps) {
    this.props = props
    this.applyStrategy()
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }
    this.destroyed = true

    this.cleanupCallbacks.forEach((cleanup) => {
      try {
        cleanup()
      } catch (error) {
        console.warn('[SDK] Renderer cleanup failed:', error)
      }
    })
    this.cleanupCallbacks.clear()

    this.restoreReplacement()
  }

  /**
   * Called by strategies when they replace the original element.
   * Stores references so we can restore on destroy.
   */
  public setReplacement(replacement: HTMLElement): void {
    this.originalElement = this.props.el
    this.replacementElement = replacement
  }

  /** Restores the crawler placeholder so another renderer can safely take over. */
  public restoreReplacement(): boolean {
    if (!this.originalElement || !this.replacementElement?.parentNode) {
      return false
    }

    this.replacementElement.parentNode.replaceChild(this.originalElement, this.replacementElement)
    this.replacementElement = null
    return true
  }

  /** Registers renderer-owned listeners/observers for the crawler lifecycle. */
  public addCleanup(cleanup: () => void): void {
    if (this.destroyed) {
      cleanup()
      return
    }

    this.cleanupCallbacks.add(cleanup)
  }

  get campaignId(): string {
    return this.props.campaignId
  }

  get config(): CrawlerElementProps['config'] {
    return this.props.config
  }

  get el(): HTMLElement {
    return this.props.el
  }

  get modalSDK(): ModalSDK {
    return this.props.modalSDK
  }

  get organizationName(): string | undefined {
    return this.props.organizationName
  }

  get pgdid(): string | undefined {
    return this.props.pgdid
  }

  get win(): Window {
    return this.props.win
  }

  get hostPageEventInstrumentation(): HostPageEventInstrumentation | undefined {
    return this.props.hostPageEventInstrumentation
  }

  get inlineGridDecision(): InlineGridDecision {
    return this.resolvedInlineGridDecision
  }

  get inlineGridProfile(): unknown {
    return this.props.inlineGridProfile
  }

  private applyStrategy() {
    /**
     * We currently allow href elements (i.e a button) to be defined
     * by the client (i.e won't have a corresponding config saved).
     *
     * So we check for these style of components first otherwise we'll get the type
     * from the config.
     */
    if (this.isClassyHref()) {
      return strategyHandlers['classyHref'](this)
    } else if (this.props.config?.type === 'inline-donation-grid') {
      return this.getInlineGridRenderer()(this)
    } else {
      return strategyHandlers['default'](this)
    }
  }

  private getInlineGridRenderer = (): InlineGridRenderer => {
    const requestedDecision = this.props.inlineGridDecision || DEFAULT_INLINE_GRID_DECISION
    const renderer = this.props.inlineGridRenderers?.[requestedDecision.path]

    if (renderer) {
      this.resolvedInlineGridDecision = requestedDecision
      return renderer
    }

    if (requestedDecision.path === 'legacy') {
      this.resolvedInlineGridDecision = requestedDecision
      return strategyHandlers['donationForm']
    }

    this.resolvedInlineGridDecision = makeInlineGridDecision('legacy', 'renderer_unavailable')
    return this.props.inlineGridRenderers?.legacy || strategyHandlers['donationForm']
  }

  private isClassyHref = (): boolean => {
    const href = this.props.el.getAttribute('href')

    return !!(href && href.indexOf(CLASSY_HREF) !== -1)
  }
}
